/**
 * Stale-chunk auto-recovery (issue #1529).
 *
 * After a deploy, an already-open tab is running an OLD index.html that
 * references hashed JS chunks the new `vite build` has replaced + removed.
 * When the tab lazy-loads one of those dead chunks, Caddy's SPA fallback used
 * to serve index.html (text/html, HTTP 200) for the missing `.js`; the browser
 * then tried to execute HTML as a module -> "'text/html' is not a valid
 * JavaScript MIME type" -> the React.lazy import rejects -> error boundary ->
 * full crash. The Caddyfile change (Parts 2/3) makes the dead asset a clean
 * 404 instead, but the user-facing win is here: on ANY chunk-load failure we
 * silently reload the tab ONCE to pull the current build.
 *
 * The reload is strictly ONE-SHOT, guarded by a sessionStorage flag so a build
 * that is genuinely broken (the chunk still 404s after reload) can never loop:
 *   - before reloading we set the flag; if it is already set we do NOT reload.
 *   - once the app has successfully mounted we clear the flag, so a LATER
 *     deploy (a fresh stale-chunk event in the same tab session) recovers too.
 *
 * Wired at bootstrap from main.tsx, before React renders.
 */

const RELOAD_FLAG = "lwp:chunk-reload";

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // sessionStorage can throw in privacy modes / sandboxed iframes.
    return null;
  }
}

/**
 * Reload the tab at most once per failed-load cycle. Returns true if a reload
 * was triggered, false if it was suppressed by the one-shot guard.
 */
export function recoverFromStaleChunk(): boolean {
  const session = safeSession();
  // If we cannot persist the guard, refuse to reload rather than risk a loop.
  if (!session) return false;

  if (session.getItem(RELOAD_FLAG)) {
    // We already reloaded once and the chunk STILL failed -> the build is
    // genuinely broken; stop here and let the error boundary show. Reloading
    // again would loop forever.
    return false;
  }

  session.setItem(RELOAD_FLAG, String(Date.now()));
  window.location.reload();
  return true;
}

/**
 * Clear the one-shot guard. Called once the app has mounted successfully, so
 * the NEXT stale-chunk event (e.g. after a future deploy in a long-lived tab)
 * is allowed to recover again.
 */
export function clearStaleChunkGuard(): void {
  safeSession()?.removeItem(RELOAD_FLAG);
}

/** Heuristic: does this rejection/error look like a failed dynamic import? */
function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof (reason as { message?: unknown }).message === "string"
          ? ((reason as { message: string }).message)
          : "";
  if (!message) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message)
  );
}

let installed = false;

/**
 * Install global listeners that auto-recover from stale chunk loads.
 *
 * Sources covered:
 *   1. Vite's `vite:preloadError` window event (fired when a preloaded chunk
 *      import fails) -- the primary, purpose-built signal.
 *   2. `unhandledrejection` whose reason looks like a failed dynamic import --
 *      catches React.lazy() rejections that are not otherwise handled.
 */
export function installChunkReloadHandler(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Primary: Vite preload-error event.
  window.addEventListener("vite:preloadError", (event) => {
    // Prevent Vite from re-throwing (which would surface as an uncaught error).
    event.preventDefault();
    recoverFromStaleChunk();
  });

  // Backstop: an unhandled React.lazy / dynamic-import rejection.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      recoverFromStaleChunk();
    }
  });
}
